/**
 * TemplateEditor — visual editor for PDF templates.
 *
 * Layout:
 *   ┌────────────┬────────────────────────────────┬────────────┐
 *   │ Placeholder│       A4 canvas                │  Selected  │
 *   │  palette   │  (letterhead iframe as bg)     │  props     │
 *   │            │  (draggable chips on top)      │  panel     │
 *   └────────────┴────────────────────────────────┴────────────┘
 *
 * Storage: positions are mm-from-top-left (see /app/backend/templates/registry.py).
 * We convert mm ↔ px using the rendered canvas width and the fixed A4
 * ratio (210:297).
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Upload, Save, Eye, Check, Trash2, Plus, X, ArrowLeft,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import {
  getTemplate, updateTemplate, uploadTemplateLetterhead,
  deleteTemplateLetterhead, activateTemplate, deactivateTemplate,
  getTemplateRegistry, fetchTemplatePreviewUrl, fetchLetterheadImageUrl,
} from '../../services/api';

const PAGE_W_MM = 210;
const PAGE_H_MM = 297;

// Snap to 1 mm grid for ergonomic placement.
const snap = (v) => Math.round(v);

const mmToPct = (mm, axis) => (mm / (axis === 'x' ? PAGE_W_MM : PAGE_H_MM)) * 100;
const pxToMm = (px, containerPx, axis) =>
  (px / containerPx) * (axis === 'x' ? PAGE_W_MM : PAGE_H_MM);

// ---------- Draggable / resizable chip ----------
const PlaceholderChip = ({
  placeholder, registryDef, containerRef, selected, onSelect, onChange, onRemove,
}) => {
  const startRef = useRef(null);
  const pos = placeholder.position || {};
  const x = pos.x ?? 20;
  const y = pos.y ?? 20;
  const w = pos.w ?? 60;
  const h = pos.h ?? 8;

  const startDrag = (e, mode /* 'move' | 'resize' */) => {
    e.stopPropagation();
    e.preventDefault();
    // Pull (clientX, clientY) from either a real mouse event or the
    // first touch — same handler now drives both, so dragging works on
    // iPad / iPhone where there ARE no mouse events.
    const point = e.touches ? e.touches[0] : e;
    const rect = containerRef.current.getBoundingClientRect();
    startRef.current = {
      mode,
      startX: point.clientX,
      startY: point.clientY,
      origX: x, origY: y, origW: w, origH: h,
      boxW: rect.width,
      boxH: rect.height,
    };
    onSelect(placeholder.key);
    const onMove = (ev) => {
      const s = startRef.current;
      if (!s) return;
      // Touch events surface clientX/Y on `ev.touches[0]`. Fall back to
      // the event itself for mouse moves.
      const p = ev.touches ? ev.touches[0] : ev;
      const dxMm = pxToMm(p.clientX - s.startX, s.boxW, 'x');
      const dyMm = pxToMm(p.clientY - s.startY, s.boxH, 'y');
      if (s.mode === 'move') {
        const nx = Math.max(0, Math.min(PAGE_W_MM - w, snap(s.origX + dxMm)));
        const ny = Math.max(0, Math.min(PAGE_H_MM - h, snap(s.origY + dyMm)));
        onChange({ ...placeholder, position: { ...pos, x: nx, y: ny } });
      } else {
        const nw = Math.max(10, Math.min(PAGE_W_MM - x, snap(s.origW + dxMm)));
        const nh = Math.max(5, Math.min(PAGE_H_MM - y, snap(s.origH + dyMm)));
        onChange({ ...placeholder, position: { ...pos, w: nw, h: nh } });
      }
    };
    const onUp = () => {
      startRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      window.removeEventListener('touchcancel', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    // `passive: false` so we can call preventDefault inside the move
    // handler if needed — without it iOS Safari swallows touchmove and
    // scrolls the page instead of dragging the chip.
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    window.addEventListener('touchcancel', onUp);
  };

  const label = registryDef?.label || placeholder.key;
  const chipColor = placeholder.key.includes('table')
    ? 'border-violet-500 bg-violet-500/10'
    : placeholder.key === 'page_x_of_y' || placeholder.key === 'generated_at'
    ? 'border-slate-500 bg-slate-500/10'
    : 'border-sky-500 bg-sky-500/10';
  const selClass = selected
    ? 'ring-2 ring-offset-1 ring-amber-500 z-20'
    : 'hover:bg-opacity-20 z-10';

  return (
    <div
      className={`absolute border-2 border-dashed ${chipColor} ${selClass} cursor-move select-none text-xs font-medium`}
      style={{
        left: `${mmToPct(x, 'x')}%`,
        top: `${mmToPct(y, 'y')}%`,
        width: `${mmToPct(w, 'x')}%`,
        height: `${mmToPct(h, 'y')}%`,
        touchAction: 'none',   // stop iOS from scroll-stealing the gesture
      }}
      onMouseDown={(e) => startDrag(e, 'move')}
      onTouchStart={(e) => startDrag(e, 'move')}
      onClick={(e) => { e.stopPropagation(); onSelect(placeholder.key); }}
      data-testid={`tpl-chip-${placeholder.key}`}
    >
      <div className="absolute inset-0 overflow-hidden px-1 py-0.5 text-slate-900 truncate pointer-events-none">
        {label}
      </div>
      {selected && (
        <>
          <button
            type="button"
            className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRemove(placeholder.key); }}
            data-testid={`tpl-chip-remove-${placeholder.key}`}
            title="Remove placeholder"
          >
            <X className="h-3 w-3" />
          </button>
          <div
            className="absolute bottom-0 right-0 w-3 h-3 bg-amber-500 cursor-nwse-resize"
            style={{ touchAction: 'none' }}
            onMouseDown={(e) => startDrag(e, 'resize')}
            onTouchStart={(e) => startDrag(e, 'resize')}
            data-testid={`tpl-chip-resize-${placeholder.key}`}
          />
        </>
      )}
    </div>
  );
};

// ---------- Inspector panel (selected chip props) ----------
const Inspector = ({ placeholder, onChange, onRemove }) => {
  if (!placeholder) {
    return (
      <div className="text-sm text-slate-500 px-3 py-4">
        Click a placeholder on the canvas to edit its style, size and position.
      </div>
    );
  }
  const pos = placeholder.position || {};
  const patch = (field, val) =>
    onChange({ ...placeholder, position: { ...pos, [field]: val } });

  return (
    <div className="px-3 py-4 space-y-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold truncate">{placeholder.key}</div>
        <Button
          size="sm" variant="ghost" className="text-red-600 h-7 px-2"
          onClick={() => onRemove(placeholder.key)}
          data-testid="inspector-remove"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <NumInput label="X (mm)" value={pos.x} onChange={(v) => patch('x', v)} min={0} max={PAGE_W_MM} />
        <NumInput label="Y (mm)" value={pos.y} onChange={(v) => patch('y', v)} min={0} max={PAGE_H_MM} />
        <NumInput label="Width (mm)" value={pos.w} onChange={(v) => patch('w', v)} min={5} max={PAGE_W_MM} />
        <NumInput label="Height (mm)" value={pos.h} onChange={(v) => patch('h', v)} min={3} max={PAGE_H_MM} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <NumInput label="Font size" value={pos.fontSize} onChange={(v) => patch('fontSize', v)} min={5} max={48} />
        <div>
          <Label className="text-xs">Align</Label>
          <select
            className="w-full border border-slate-200 rounded-md h-9 px-2 text-sm"
            value={pos.align || 'left'}
            onChange={(e) => patch('align', e.target.value)}
            data-testid="inspector-align"
          >
            <option value="left">Left</option>
            <option value="center">Centre</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <Checkbox checked={!!pos.bold} onCheckedChange={(v) => patch('bold', !!v)} data-testid="inspector-bold" />
          <span>Bold</span>
        </label>
        <label className="flex items-center gap-2">
          <Checkbox checked={!!pos.italic} onCheckedChange={(v) => patch('italic', !!v)} data-testid="inspector-italic" />
          <span>Italic</span>
        </label>
      </div>

      <div>
        <Label className="text-xs">Colour (hex)</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={pos.color || '#0f172a'}
            onChange={(e) => patch('color', e.target.value)}
            className="h-9 w-12 border border-slate-200 rounded-md cursor-pointer"
            data-testid="inspector-color"
          />
          <Input
            value={pos.color || '#0f172a'}
            onChange={(e) => patch('color', e.target.value)}
            className="h-9 text-xs"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Scope</Label>
        <select
          className="w-full border border-slate-200 rounded-md h-9 px-2 text-sm"
          value={placeholder.scope || 'first'}
          onChange={(e) => onChange({ ...placeholder, scope: e.target.value })}
          data-testid="inspector-scope"
        >
          <option value="first">First page only</option>
          <option value="every">Every page</option>
        </select>
      </div>
    </div>
  );
};

const NumInput = ({ label, value, onChange, min, max }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input
      type="number"
      min={min} max={max}
      value={value ?? ''}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-9 text-sm"
    />
  </div>
);

// Default API surface — operates against `/api/pdf-templates/...`. The
// editor is parameterised by an `adapter` so it can also drive shared
// letterheads (`/api/pdf-letterheads/...`) without forking the UI.
const DEFAULT_TEMPLATE_ADAPTER = {
  getOne: getTemplate,
  update: updateTemplate,
  uploadFile: uploadTemplateLetterhead,
  deleteFile: deleteTemplateLetterhead,
  fetchPreviewUrl: fetchTemplatePreviewUrl,
  activate: activateTemplate,
  deactivate: deactivateTemplate,
  supportsActivate: true,
  fixedKind: null,            // editor reads kind from the doc
  letterheadLabel: 'letterhead',
};

// ---------- Main editor ----------
export const TemplateEditor = ({ templateId, onBack, adapter: adapterProp }) => {
  const adapter = adapterProp || DEFAULT_TEMPLATE_ADAPTER;
  const [tpl, setTpl] = useState(null);
  const [registry, setRegistry] = useState({});
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  // Cache-buster for the rasterised letterhead PNG. Bumping it on
  // upload forces the `<img>` to refetch; otherwise the browser would
  // happily reuse the previous PDF's render.
  const [letterheadBust, setLetterheadBust] = useState(() => Date.now());
  // Authenticated blob URL for the rasterised letterhead. `<img>` can't
  // send the bearer token, so we fetch via axios → blob → object URL.
  const [letterheadImgUrl, setLetterheadImgUrl] = useState(null);
  const canvasRef = useRef(null);
  const pdf = usePdfPreview();

  // Resolve / refresh the letterhead image whenever the template
  // (re)mounts, the underlying letterhead changes, or the user uploads
  // a new PDF (cache-buster).
  useEffect(() => {
    let alive = true;
    let createdUrl = null;
    if (!tpl) return undefined;
    if (!tpl.letterheadFile && !tpl.letterheadId) {
      setLetterheadImgUrl(null);
      return undefined;
    }
    fetchLetterheadImageUrl(tpl.id, !!adapter.fixedKind && adapter.fixedKind === 'letterhead')
      .then((url) => {
        if (!alive) {
          if (url) try { URL.revokeObjectURL(url); } catch { /* noop */ }
          return;
        }
        createdUrl = url;
        setLetterheadImgUrl(url);
      })
      .catch(() => { if (alive) setLetterheadImgUrl(null); });
    return () => {
      alive = false;
      if (createdUrl) try { URL.revokeObjectURL(createdUrl); } catch { /* noop */ }
    };
  }, [tpl?.id, tpl?.letterheadFile, tpl?.letterheadId, letterheadBust, adapter.fixedKind]);

  useEffect(() => {
    (async () => {
      const [t, reg] = await Promise.all([
        adapter.getOne(templateId),
        getTemplateRegistry(),
      ]);
      // For letterhead-mode the doc has no `kind` field — synthesise
      // it so `kindDef`/registry lookup hits the right entry.
      if (adapter.fixedKind && !t.kind) t.kind = adapter.fixedKind;
      setTpl(t);
      setRegistry(reg.registry || {});
    })();
  }, [templateId, adapter]);

  const kindDef = tpl && registry[tpl.kind];
  const placeholderDefs = kindDef?.placeholders || [];
  const placedKeys = useMemo(() => new Set((tpl?.placeholders || []).map(p => p.key)), [tpl]);

  const updatePlaceholder = useCallback((next) => {
    setTpl((prev) => ({
      ...prev,
      placeholders: prev.placeholders.map(p => p.key === next.key ? next : p),
    }));
    setDirty(true);
  }, []);

  const removePlaceholder = useCallback((key) => {
    setTpl((prev) => ({
      ...prev,
      placeholders: prev.placeholders.filter(p => p.key !== key),
    }));
    if (selected === key) setSelected(null);
    setDirty(true);
  }, [selected]);

  const addPlaceholder = (def) => {
    if (placedKeys.has(def.key)) return;
    const next = { key: def.key, scope: def.scope || 'first', position: { ...def.default } };
    setTpl((prev) => ({ ...prev, placeholders: [...prev.placeholders, next] }));
    setSelected(def.key);
    setDirty(true);
  };

  const save = async () => {
    if (!tpl) return;
    setSaving(true);
    try {
      const updated = await adapter.update(tpl.id, {
        name: tpl.name,
        placeholders: tpl.placeholders,
        margins: tpl.margins,
      });
      if (adapter.fixedKind && !updated.kind) updated.kind = adapter.fixedKind;
      setTpl(updated);
      setDirty(false);
      toast.success('Template saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadLetterhead = async (file) => {
    if (!file) return;
    setUploadBusy(true);
    try {
      const updated = await adapter.uploadFile(tpl.id, file);
      if (adapter.fixedKind && !updated.kind) updated.kind = adapter.fixedKind;
      setTpl(updated);
      // Cache-bust the rasteriser so the <img> picks up the new PDF.
      setLetterheadBust(Date.now());
      toast.success(`Letterhead uploaded (${updated.letterheadPages} page${updated.letterheadPages !== 1 ? 's' : ''})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadBusy(false);
    }
  };

  const removeLetterhead = async () => {
    if (!window.confirm('Remove the uploaded letterhead PDF? Placeholders will be drawn on a blank A4.')) return;
    try {
      const updated = await adapter.deleteFile(tpl.id);
      if (adapter.fixedKind && !updated.kind) updated.kind = adapter.fixedKind;
      setTpl(updated);
      toast.success('Letterhead removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  const toggleActive = async () => {
    if (!adapter.supportsActivate) return;
    try {
      const updated = tpl.active
        ? await adapter.deactivate(tpl.id)
        : await adapter.activate(tpl.id);
      setTpl(updated);
      toast.success(updated.active ? 'Template set as active' : 'Template deactivated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  if (!tpl) {
    return <div className="p-6 text-slate-500">Loading template…</div>;
  }

  const selectedPh = tpl.placeholders.find(p => p.key === selected);

  // Letterhead background — rasterised PNG of page 1, fetched via
  // authenticated blob URL above. Rendering as an `<img>` keeps chip
  // and background scaling in lockstep at every browser zoom level
  // (the previous `<iframe>` let the native PDF viewer scale
  // independently, which is the bug the user reported).
  const letterheadImageSrc = letterheadImgUrl;

  return (
    <div className="flex flex-col h-full min-h-[80vh]">
      {/* Mobile notice — the drag-drop canvas needs room. Keep management
          (back / preview / activate) available, but hide the palette +
          inspector panes below md. */}
      <div className="md:hidden p-4 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
        <div className="font-semibold mb-1">Desktop recommended</div>
        <div className="text-xs leading-snug">
          The visual editor needs a larger screen to drag placeholder chips
          accurately. You can still preview, activate or go back from here.
        </div>
      </div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2 min-w-0">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="tpl-editor-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Input
            value={tpl.name}
            onChange={(e) => { setTpl({ ...tpl, name: e.target.value }); setDirty(true); }}
            className="h-8 text-sm max-w-xs"
            data-testid="tpl-name-input"
          />
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{tpl.kind}</span>
          {tpl.active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
              <Check className="h-3 w-3" /> Active
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex">
            <Button
              size="sm" variant="outline"
              onClick={(e) => { e.currentTarget.previousElementSibling?.click?.(); }}
              asChild
            >
              <span className="cursor-pointer">
                <Upload className="h-4 w-4 mr-1" />
                {uploadBusy ? 'Uploading…' : (tpl.letterheadFile ? 'Replace letterhead' : 'Upload letterhead PDF')}
                <input
                  type="file" accept="application/pdf" hidden
                  onChange={(e) => { uploadLetterhead(e.target.files?.[0]); e.target.value = ''; }}
                  data-testid="tpl-letterhead-upload"
                />
              </span>
            </Button>
          </label>
          {tpl.letterheadFile && (
            <Button size="sm" variant="ghost" className="text-red-600" onClick={removeLetterhead} data-testid="tpl-letterhead-remove">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => pdf.preview(() => adapter.fetchPreviewUrl(tpl.id))}
            data-testid="tpl-preview-btn"
          >
            <Eye className="h-4 w-4 mr-1" /> Preview
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !dirty}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="tpl-save-btn"
          >
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : (dirty ? 'Save' : 'Saved')}
          </Button>
          {adapter.supportsActivate && (
            <Button
              size="sm"
              onClick={toggleActive}
              className={tpl.active ? 'bg-slate-200 hover:bg-slate-300 text-slate-900' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
              data-testid="tpl-toggle-active"
            >
              {tpl.active ? 'Deactivate' : 'Set as active'}
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Palette — hidden on mobile (dragging doesn't work well without a mouse). */}
        <aside className="hidden md:block w-56 border-r border-slate-200 bg-slate-50 overflow-y-auto">
          <div className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Available placeholders
          </div>
          <div className="px-2 pb-4 space-y-1">
            {placeholderDefs.map((def) => {
              const placed = placedKeys.has(def.key);
              return (
                <button
                  key={def.key}
                  disabled={placed}
                  onClick={() => addPlaceholder(def)}
                  className={`w-full flex items-center justify-between text-left px-2 py-1.5 rounded text-xs
                    ${placed
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-white border border-slate-200 hover:border-sky-500 hover:bg-sky-50 cursor-pointer'}`}
                  data-testid={`tpl-palette-${def.key}`}
                >
                  <span className="truncate">{def.label}</span>
                  {placed ? <Check className="h-3 w-3 shrink-0" /> : <Plus className="h-3 w-3 shrink-0" />}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Canvas */}
        <main className="flex-1 overflow-auto bg-slate-200 p-4 flex items-start justify-center">
          <div
            ref={canvasRef}
            className="relative bg-white shadow-xl"
            style={{ width: 'min(100%, 720px)', aspectRatio: `${PAGE_W_MM} / ${PAGE_H_MM}` }}
            onClick={() => setSelected(null)}
            data-testid="tpl-canvas"
          >
            {letterheadImageSrc ? (
              <img
                key={letterheadImageSrc}
                src={letterheadImageSrc}
                alt="Letterhead background"
                className="absolute inset-0 w-full h-full object-fill pointer-events-none select-none"
                draggable={false}
                data-testid="tpl-letterhead-image"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                No letterhead uploaded. Placeholders will be drawn on a blank A4.
              </div>
            )}
            {/* Margin guides */}
            {tpl.margins && (
              <div
                className="absolute border border-dashed border-amber-300/70 pointer-events-none"
                style={{
                  left: `${mmToPct(tpl.margins.left ?? 20, 'x')}%`,
                  top: `${mmToPct(tpl.margins.top ?? 18, 'y')}%`,
                  right: `${mmToPct(tpl.margins.right ?? 20, 'x')}%`,
                  bottom: `${mmToPct(tpl.margins.bottom ?? 18, 'y')}%`,
                }}
              />
            )}
            {/* Chips */}
            {(tpl.placeholders || []).map((p) => {
              const def = placeholderDefs.find(d => d.key === p.key);
              return (
                <PlaceholderChip
                  key={p.key}
                  placeholder={p}
                  registryDef={def}
                  containerRef={canvasRef}
                  selected={selected === p.key}
                  onSelect={setSelected}
                  onChange={updatePlaceholder}
                  onRemove={removePlaceholder}
                />
              );
            })}
          </div>
        </main>

        {/* Inspector — hidden on mobile. */}
        <aside className="hidden md:block w-72 border-l border-slate-200 bg-white overflow-y-auto">
          <div className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            Properties
          </div>
          <Inspector
            placeholder={selectedPh}
            onChange={updatePlaceholder}
            onRemove={removePlaceholder}
          />

          <div className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 border-t border-b border-slate-200">
            Page margins (mm)
          </div>
          <div className="px-3 py-3 grid grid-cols-2 gap-2">
            {['top', 'right', 'bottom', 'left'].map((side) => (
              <NumInput
                key={side}
                label={side.charAt(0).toUpperCase() + side.slice(1)}
                value={tpl.margins?.[side] ?? 20}
                onChange={(v) => { setTpl({ ...tpl, margins: { ...(tpl.margins || {}), [side]: v } }); setDirty(true); }}
                min={0} max={60}
              />
            ))}
          </div>
        </aside>
      </div>

      <PdfPreviewDialog
        url={pdf.url}
        filename={pdf.filename}
        onClose={pdf.close}
        title="Template preview"
      />
    </div>
  );
};

export default TemplateEditor;
